import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import "./ArticleComponent.css";
import Carousel from "./Carousel";
import { CarouselLib } from "./CarouselLib";
import CardList from "./CardList";
import Card from "./CardItem";
import PageHeader from "./PageHeader";
import CallToActionWithModal from "./CallToActionWithModal";
import CTAWhatsApp from "./CTAWhatsApp";
import TabbedCardList from "./TabbedCardList";
import IconBulletList from "./IconBulletList";
import ProjectsGrid from "./ProjectsGrid";
import ImageCompare from "./ImageCompare";
import EngineStagesSection from "./EngineStagesSection";
import ParallaxSection from "./ParallaxSection";
import { Helmet } from "react-helmet";
import {
  Headline,
  Paragraph,
  HighlightedQuote,
  Section,
  ContentBlock,
  TextSectionHero,
  TextSection,
  GalleryGrid,
  SimpleList,
} from "./ui";
import LighthouseGrid from "./LighthouseGrid";
import HeroSection from "./HeroSection";
import InspectionPricingTable from "./InspectionPricingTable";
import InspectionPricingSearch from "./InspectionPricingSearch";
import SplitScreenLayout from "./SplitScreenLayout";
import StickyHeadline from "./StickyHeadline";

const ArticleComponent = ({
  title,
  titleImageUrl,
  showTitle,
  showHeader,
  content,
  theme = "dark",
  isFirstArticle,
  meta,
}) => {
  // Helper to build image URL for titleImageUrl (supports both local paths and Cloudinary public IDs)
  const getImageUrl = (src) => {
    if (!src) return null;

    // If already a full URL, use as-is
    if (src.startsWith("http")) return src;

    // If it looks like a Cloudinary public ID, build Cloudinary URL
    const cloudName = process.env.REACT_APP_CLOUDINARY_NAME;
    if (
      cloudName &&
      (src.includes("gallery/") || (src.includes("_") && !src.match(/\.\w+$/)))
    ) {
      const publicId = src.startsWith("/") ? src.substring(1) : src;
      return `https://res.cloudinary.com/${cloudName}/image/upload/q_auto:best/f_webp/${publicId}`;
    }

    // Fallback to local path
    return `${process.env.PUBLIC_URL}/${src}`;
  };

  const finalImageUrl = getImageUrl(titleImageUrl);


  // Hook to detect mobile screen size
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768); // Adjust breakpoint as needed
    };

    // Check on mount
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const items = [
    {
      id: "slide-1",
      image: `${process.env.PUBLIC_URL}/wtm-hero-3.jpg`,
      alt: "Slide 1",
      headline: "Slide 1 Headline",
      text: "WTM Sportwagen bietet spezialisierte Motorrevisionen für luftgekühlte Porsche-Modelle. Unser Team verbindet traditionelles Handwerk und moderne Technik, um die Authentizität und Performance Ihres Porsche zu bewahren.",
      link: "/slide1",
      buttonText: "Learn More",
    },
    {
      id: "slide-2",
      image: `${process.env.PUBLIC_URL}/wtm-hero-3.jpg`,
      alt: "Slide 2",
      headline: "Slide 2 Headline",
      text: "WTM Sportwagen bietet spezialisierte Motorrevisionen für luftgekühlte Porsche-Modelle. Unser Team verbindet traditionelles Handwerk und moderne Technik, um die Authentizität und Performance Ihres Porsche zu bewahren.",
      link: "/slide2",
      buttonText: "Learn More",
    },
    {
      id: "slide-3",
      image: `${process.env.PUBLIC_URL}/wtm-hero-3.jpg`,
      alt: "Slide 3",
      headline: "Slide 3 Headline",
      text: "WTM Sportwagen bietet spezialisierte Motorrevisionen für luftgekühlte Porsche-Modelle. Unser Team verbindet traditionelles Handwerk und moderne Technik, um die Authentizität und Performance Ihres Porsche zu bewahren.",
      link: "/slide3",
      buttonText: "Learn More",
    },
  ];

  return (
    <>
      <Helmet>
        <title>{meta?.title || title || "WTM Sportwagen"}</title>
        <meta name="description" content={meta?.description || ""} />
        <meta
          property="og:title"
          content={meta?.title || title || "WTM Sportwagen"}
        />
        <meta property="og:description" content={meta?.description || ""} />
        <meta property="og:type" content="article" />
        {finalImageUrl && <meta property="og:image" content={finalImageUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={meta?.title || title || "WTM Sportwagen"}
        />
        <meta name="twitter:description" content={meta?.description || ""} />
        {finalImageUrl && <meta name="twitter:image" content={finalImageUrl} />}
      </Helmet>
      {showHeader && (
        <PageHeader
          imageUrl={finalImageUrl}
          headline={null}
          isFirstArticle={isFirstArticle}
        />
      )}
      <div className={`article-page ${theme}`}>
        {/* {showTitle && <h2 className="text-4xl font-semibold text-gray-900 mb-6 leading-relaxed text-left">{title}</h2>} */}
        {content.map((section, index) => {
          switch (section.type) {
            case "headline":
              return (
                <Section
                  key={`headline-${index}`}
                  variant="transparent"
                  spacing="none"
                >
                  <ContentBlock
                    maxWidth="4xl"
                    spacing="comfortable"
                    className="mt-16"
                  >
                    <Headline variant="h2" color="secondary">
                      {section.text}
                    </Headline>
                  </ContentBlock>
                </Section>
              );
            case "sub-headline":
              return (
                <Section
                  key={`subheadline-${index}`}
                  variant="transparent"
                  spacing="none"
                >
                  <ContentBlock maxWidth="4xl" spacing="comfortable">
                    <Headline variant="h5" color="secondary">
                      {section.text}
                    </Headline>
                  </ContentBlock>
                </Section>
              );
            case "paragraph":
              return (
                <Section
                  key={`paragraph-${index}`}
                  variant="transparent"
                  spacing={section.spacing || "paragraph"}
                  className={section.className || ""}
                  style={section.style || {}}
                >
                  <ContentBlock
                    maxWidth="4xl"
                    spacing="comfortable"
                    className="mx-auto content-area-dark"
                  >
                    <Paragraph
                      variant="large"
                      color="secondary"
                      dangerouslySetInnerHTML={
                        typeof section.text === "string"
                          ? { __html: section.text }
                          : undefined
                      }
                    >
                      {typeof section.text === "string"
                        ? undefined
                        : section.text}
                    </Paragraph>
                  </ContentBlock>
                </Section>
              );
            case "large-paragraph":
              return (
                <Section
                  key={`large-paragraph-${index}`}
                  variant="transparent"
                  spacing="none"
                >
                  <ContentBlock
                    maxWidth="4xl"
                    spacing="comfortable"
                    className="content-area-dark"
                  >
                    <Paragraph
                      variant="medium"
                      color="secondary"
                      dangerouslySetInnerHTML={
                        typeof section.text === "string"
                          ? { __html: section.text }
                          : undefined
                      }
                    >
                      {typeof section.text === "string"
                        ? undefined
                        : section.text}
                    </Paragraph>
                  </ContentBlock>
                </Section>
              );
            case "small-paragraph":
              return (
                <Section
                  key={`small-paragraph-${index}`}
                  variant="transparent"
                  spacing="none"
                >
                  <ContentBlock
                    maxWidth="4xl"
                    spacing="comfortable"
                    className="pb-12 content-area-dark"
                  >
                    <Paragraph
                      variant="medium"
                      color="secondary"
                      dangerouslySetInnerHTML={
                        typeof section.text === "string"
                          ? { __html: section.text }
                          : undefined
                      }
                    >
                      {typeof section.text === "string"
                        ? undefined
                        : section.text}
                    </Paragraph>
                  </ContentBlock>
                </Section>
              );
            case "list":
              return (
                <SimpleList
                  key={`list-${index}`}
                  items={section.items || []}
                  variant={section.variant || "transparent"}
                  spacing={section.spacing || "none"}
                  maxWidth={section.maxWidth || "4xl"}
                  contentSpacing={section.contentSpacing || "comfortable"}
                  bulletColor={section.bulletColor || "text-yellow-400"}
                  bulletStyle={section.bulletStyle || "•"}
                  className={section.className || ""}
                  color={section.color || "var(--color-primary)"}
                  style={section.style || {}}
                />
              );
            case "hero_section":
              return (
                <Section
                  key={`hero-section-${index}`}
                  variant="transparent"
                  spacing="comfortable"
                >
                  <ContentBlock
                    maxWidth="6xl"
                    spacing="comfortable"
                    className="mx-auto"
                  >
                    <div className="hero-section-container mb-12">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        {/* Image */}
                        <div className="order-2 lg:order-2 sm:mt-20">
                          <img
                            src={`${process.env.PUBLIC_URL}/${section.imageUrl}`}
                            alt={section.alt || "Hero image"}
                            className="w-full h-auto rounded-lg shadow-lg"
                            style={{
                              maxHeight: section.maxHeight || "400px",
                              objectFit: "cover",
                            }}
                          />
                          {section.caption && (
                            <p className="text-sm text-gray-400 mt-2 text-center italic">
                              {section.caption}
                            </p>
                          )}
                        </div>

                        {/* Text Content */}
                        <div className="order-1 lg:order-1">
                          {section.headline && (
                            <Headline
                              variant="h2"
                              color="secondary"
                              className="pt-12"
                            >
                              {section.headline}
                            </Headline>
                          )}
                          {section.paragraphs &&
                            section.paragraphs.map((paragraph, pIndex) => (
                              <Paragraph
                                key={`hero-paragraph-${pIndex}`}
                                variant="small"
                                color="secondary"
                                dangerouslySetInnerHTML={{ __html: paragraph }}
                              />
                            ))}
                        </div>
                      </div>
                    </div>
                  </ContentBlock>
                </Section>
              );
            case "text_section_hero":
              return (
                <TextSectionHero
                  key={`text-section-hero-${index}`}
                  size={section.size || "md"}
                  annotation={section.annotation}
                  headline={section.headline}
                  lead={section.lead}
                  highlight={section.highlight}
                  variant={section.variant || "secondary"}
                  spacing={section.spacing || "spacious"}
                  maxWidth={section.maxWidth || "4xl"}
                  align={section.align || "left"}
                  className={section.className || ""}
                  margin={section.margin !== false}
                />
              );
            case "text_section":
              return (
                <TextSection
                  key={`text-section-${index}`}
                  headline={section.headline}
                  lead={section.lead}
                  text={section.text}
                  className={section.className || ""}
                  margin={section.margin !== false}
                  showButtons={section.showButtons || false}
                  primaryButton={section.primaryButton}
                  secondaryButton={section.secondaryButton}
                  whatsappButton={section.whatsappButton || false}
                  whatsappNumber={section.whatsappNumber}
                  whatsappText={section.whatsappText}
                />
              );
            case "gallery_grid":
              return (
                <Section
                  key={`gallery-grid-${index}`}
                  variant={section.variant || "transparent"}
                  spacing={section.spacing || "comfortable"}
                  margin={section.margin !== false}
                  className={section.className || ""}
                >
                  <ContentBlock
                    maxWidth={section.maxWidth || "none"}
                    spacing={section.contentSpacing || "none"}
                    className={section.contentClassName || ""}
                  >
                    <GalleryGrid
                      images={section.images || []}
                      maxHeight={section.maxHeight || "600px"}
                      animationSpeed={section.animationSpeed || "3000"}
                    />
                  </ContentBlock>
                </Section>
              );
            case "parallax_section":
              return (
                <ParallaxSection
                  key={`parallax-section-${index}`}
                  imageUrl={
                    section.imageUrl
                      ? `${process.env.PUBLIC_URL}/${section.imageUrl}`
                      : undefined
                  }
                  className={section.className || ""}
                >
                  {section.content && (
                    <div
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  )}
                  {section.sections &&
                    section.sections.map((nestedSection, nestedIndex) => {
                      // Render nested sections within the parallax
                      const nestedKey = `parallax-nested-${index}-${nestedIndex}`;
                      return (
                        <div key={nestedKey}>
                          {/* This could recursively render other section types */}
                          {nestedSection.type === "text_section_hero" && (
                            <TextSectionHero
                              annotation={nestedSection.annotation}
                              headline={nestedSection.headline}
                              lead={nestedSection.lead}
                              highlight={nestedSection.highlight}
                              variant="transparent"
                              spacing="none"
                              size={nestedSection.size}
                            />
                          )}
                          {nestedSection.type === "cta-whatsapp" && (
                            <CTAWhatsApp />
                          )}
                        </div>
                      );
                    })}
                </ParallaxSection>
              );
            case "carousel":
              return (
                <Section
                  key={`carousel-${index}`}
                  variant="transparent"
                  spacing="none"
                >
                  <ContentBlock maxWidth="4xl" spacing="comfortable">
                    <Carousel images={section.images} />
                  </ContentBlock>
                </Section>
              );
            case "card-list":
              if (isMobile) {
                return (
                  <Section
                    key={`cardlist-tabbed-${index}`}
                    variant="transparent"
                    spacing="none"
                  >
                    <ContentBlock
                      maxWidth="4xl"
                      spacing="comfortable"
                      className="pt-4"
                    >
                      <TabbedCardList
                        cards={section.cards}
                        theme={section.theme}
                        color={section.color || "var(--color-primary)"}
                        noImage={section.noImage || false}
                      />
                    </ContentBlock>
                  </Section>
                );
              } else {
                const cards = section.cards.map((card) => ({
                  id: card.id,
                  Component: ({ noImage }) => (
                    <Card
                      headline={card.headline}
                      introduction={card.introduction}
                      keyPoints={card.keyPoints}
                      image={card.image}
                      badge={card.badge}
                      color={section.color || "primary"}
                      noImage={noImage}
                    />
                  ),
                }));
                return (
                  <Section
                    key={`cardlist-${index}`}
                    variant="transparent"
                    spacing="none"
                  >
                    <ContentBlock maxWidth="none" spacing="comfortable">
                      <CardList
                        cards={cards}
                        theme={section.theme}
                        color={section.color || "primary"}
                        noImage={section.noImage || false}
                      />
                    </ContentBlock>
                  </Section>
                );
              }

            case "cta":
              return (
                <Section
                  key={`cta-${index}`}
                  variant="transparent"
                  spacing="none"
                >
                  <ContentBlock maxWidth="4xl" spacing="comfortable" />
                </Section>
              );
            case "cta-with-modal":
              return (
                <CallToActionWithModal
                  key={`cta-with-modal-${index}`}
                  darkMode={section.darkMode || false}
                  headline={section.headline}
                  paragraph={section.paragraph}
                  spacing={section.spacing || "spacious"}
                />
              );
            case "cta-whatsapp":
              return (
                <Section
                  key={`cta-whatsapp-${index}`}
                  variant={section.variant || "transparent"}
                  spacing={section.spacing || "comfortable"}
                  margin={section.margin !== false}
                >
                  <ContentBlock
                    maxWidth={section.maxWidth || "4xl"}
                    spacing={section.contentSpacing || "comfortable"}
                    className={`mx-auto ${section.contentClassName || ""}`}
                  >
                    <CTAWhatsApp
                      firstParagraph={section.headline}
                      secondParagraph={section.paragraph}
                      buttonText={section.buttonText}
                    />
                  </ContentBlock>
                </Section>
              );
            case "icon-bullet-list":
              return (
                <IconBulletList
                  key={`icon-bullet-list-${index}`}
                  items={section.items}
                  icon={section.icon}
                  headline={section.headline}
                  headlineIcon={section.headlineIcon}
                  highlightedIndices={section.highlightedIndices}
                  variant={section.variant || "transparent"}
                  spacing={section.spacing || "none"}
                  maxWidth={section.maxWidth || "4xl"}
                  className={section.className || ""}
                  margin={section.margin}
                />
              );
            case "highlighted-paragraph":
              return (
                <Section
                  key={`highlighted-paragraph-${index}`}
                  variant="transparent"
                  spacing="compact"
                  style={section.style || {}}
                >
                  <ContentBlock
                    maxWidth="4xl"
                    spacing="comfortable"
                    className="mt-12 mx-auto"
                  >
                    <HighlightedQuote
                      variant="medium"
                      color="secondary"
                      borderHeight="tall"
                      dangerouslySetInnerHTML={
                        typeof section.text === "string"
                          ? { __html: section.text }
                          : undefined
                      }
                    >
                      {typeof section.text === "string"
                        ? undefined
                        : section.text}
                    </HighlightedQuote>
                  </ContentBlock>
                </Section>
              );
            case "divider":
              return (
                <Section
                  key={`divider-${index}`}
                  variant="transparent"
                  spacing="none"
                >
                  <ContentBlock maxWidth="4xl" spacing="comfortable">
                    <div className="my-12 flex justify-center">
                      <div
                        className="h-px w-full max-w-lg opacity-30"
                        style={{ backgroundColor: "var(--color-highlight)" }}
                      ></div>
                    </div>
                  </ContentBlock>
                </Section>
              );
            case "relevant-posts":
              return (
                <div key={`relevant-posts-${index}`}>
                  <ProjectsGrid
                    margin={false}
                    showAll={false}
                    postIds={section.postIds}
                    postSlugs={section.postSlugs}
                    showIntro={true}
                    introText={section.introText}
                    header={section.headline}
                    darkMode={section.darkMode}
                    parentPath={location.pathname}
                    renderStickyHeadline={true}
                    stickyHeadline="Aktuelles aus der Werkstatt"
                  />
                </div>
              );
            case "image-compare":
              return (
                <ImageCompare
                  key={`image-compare-${index}`}
                  beforeSrc={`${process.env.PUBLIC_URL}/${section.beforeImage}`}
                  afterSrc={`${process.env.PUBLIC_URL}/${section.afterImage}`}
                  title={section.title}
                  leftLabel={section.leftLabel}
                  rightLabel={section.rightLabel}
                  variant={section.variant || "secondary"}
                  spacing={section.spacing || "comfortable"}
                  className={section.className || ""}
                />
              );
            case "split_screen_layout":
              return (
                <SplitScreenLayout
                  key={`split-screen-${index}`}
                  leftImage={getImageUrl(section.leftImage)}
                  rightContent={section.rightContent}
                  imageAlt={section.imageAlt || "Split screen image"}
                  navbarHeight={section.navbarHeight || 70}
                  headline={section.headline}
                  headlineClassName={section.headlineClassName || ""}
                  className={section.className || ""}
                />
              );
            case "engine-stages":
              return (
                <div key={`engine-stages-${index}`}>
                  <EngineStagesSection />
                </div>
              );
            case "lighthouse-grid":
              return (
                <div key={`lighthouse-grid-wrapper-${index}`}>
                  {section.stickyHeadline && (
                    <StickyHeadline
                      headline={section.stickyHeadline}
                      navbarHeight={section.navbarHeight || 70}
                    />
                  )}
                  <Section
                    key={`lighthouse-grid-${index}`}
                    variant={section.variant || "transparent"}
                    spacing={section.spacing || "comfortable"}
                  >
                    <ContentBlock
                      maxWidth={section.maxWidth || "7xl"}
                      spacing={section.contentSpacing || "tight"}
                    >
                      <LighthouseGrid
                        images={section.images}
                        className={section.className || ""}
                        showThumbnails={section.showThumbnails !== false}
                        lightboxKey={section.lightboxKey}
                        imageWidth={section.imageWidth || 800}
                        imageCrop={section.imageCrop || "fill"}
                        imageGravity={section.imageGravity || "center"}
                        highQuality={section.highQuality || false}
                        showHeader={section.showHeader || false}
                        title={section.title || ""}
                        subtitle={section.subtitle || ""}
                        description={section.description || ""}
                      />
                    </ContentBlock>
                  </Section>
                </div>
              );
            case "hero_image":
              return (
                <HeroSection
                  key={`hero-image-${index}`}
                  backgroundImage={getImageUrl(section.backgroundImage)}
                  headline={section.headline}
                  description={section.description}
                  hasVignette={section.hasVignette !== false}
                  overlayOpacity={section.overlayOpacity || 0.4}
                  className={section.className || ""}
                  style={section.style || {}}
                />
              );
            case "inspection-pricing-table":
              return (
                <Section
                  key={`inspection-pricing-table-${index}`}
                  variant={section.variant || "primary"}
                  spacing={section.spacing || "comfortable"}
                  margin={section.margin !== false}
                  className={section.className || ""}
                >
                  <ContentBlock
                    maxWidth={section.maxWidth || "7xl"}
                    spacing={section.contentSpacing || "comfortable"}
                    className={section.contentClassName || ""}
                  >
                    <InspectionPricingTable />
                  </ContentBlock>
                </Section>
              );
            case "inspection-pricing-search":
              return (
                <Section
                  key={`inspection-pricing-search-${index}`}
                  variant={section.variant || "primary"}
                  spacing={section.spacing || "comfortable"}
                  margin={section.margin !== false}
                  className={section.className || ""}
                >
                  <ContentBlock
                    maxWidth={section.maxWidth || "4xl"}
                    spacing={section.contentSpacing || "comfortable"}
                    className={section.contentClassName || ""}
                  >
                    <InspectionPricingSearch />
                  </ContentBlock>
                </Section>
              );
            default:
              return null;
          }
        })}
      </div>
    </>
  );
};

export default ArticleComponent;
